import React from 'react';
import styled, { css } from 'styled-components';
import { createPortal } from 'react-dom';
import Close from '@zocdoc-frontend-common/assets-icons/Close';
import { breakpoint } from '@zocdoc-frontend-common/styles-grid';
import { Button, VARIATION } from '@zocdoc-frontend-common/components-button';
import { ScrollLock } from '@zocdoc-frontend-common/utils-scrolllock';
import { blue100, white } from '@zocdoc-mezzanine/foundations-colors';
import {
    headingMedium,
    headingLarge,
    labelMedium,
} from '@zocdoc-mezzanine/foundations-typography';
import useModal from './hooks/useModal';
import withDomNode from './hooks/withDomNode';
import Overlay from './Overlay';

/**
 * InternalModal
 * This component should only be used internally by the Modal component. Consumers should use BaseModal or CompactModal instead.
 * @param {JSX.Element} domNode - DOM node to attach the modal to.
 * @param {string} title - Title of your modal which will be automatically styled.
 * @param {JSX.Element} children - Components to render inside the modal.
 * @param {string} className - Classname which can be used in some cases to propegate your styles to the styled Content component.
 * @param {boolean} ignoreClickOutside - if true, clicking outside of the modal content will not close the modal.
 * @param {boolean} isCompact - Do not use. This prop is only to be used internally by the Modal component.
 * @param {function} onClose - Callback which is triggered to close the modal. If this is undefined, no close icon will be rendered.
 * @param {boolean} [useScrollLock=true] - If true, the content underneath the modal can not be scrolled while the modal is open.
 * @param {boolean} whiteOverlay - indicates whether to use white overlay. This will be deprecated soon.
 * This is only used for vaccine related features and will eventually be torn down.
 * @param {Modal.Button} primaryButtonProps - Props for the primary button. If this is null\undefined, the onClose prop must be set.
 * @param {Modal.Button} secondaryButtonProps - Props for the secondary button. If this is set, the primaryButton prop must also be set.
 * @param {React.Ref} [overrideAutofocusRef] - ref attached to an element in the modal content which will get auto-focus if it is fully visible. This will take precedence over the close icon and the title.
 * @returns {JSX.Element} InternalModal
 */
function InternalModal({
    'aria-label': ariaLabel,
    'aria-describedby': ariaDescribedby,
    isCompact,
    domNode,
    title,
    children,
    className,
    'data-test': dataTest,
    ignoreClickOutside,
    onClose,
    useScrollLock = true,
    whiteOverlay,
    primaryButtonProps,
    secondaryButtonProps,
    overrideAutofocusRef,
}) {
    const {
        contentRef,
        closeRef,
        titleRef,
        handleKeyDownCloseIcon,
        handleClickOverlay,
    } = useModal({
        title,
        ignoreClickOutside,
        onClose,
        whiteOverlay,
        primaryButtonProps,
        secondaryButtonProps,
        overrideAutofocusRef,
    });

    const child = (
        <>
            <Overlay onClick={handleClickOverlay} whiteOverlay={whiteOverlay}>
                <Wrapper
                    $isCompact={isCompact}
                    aria-describedby={ariaDescribedby}
                    aria-label={ariaLabel}
                    aria-labelledby="modal-title"
                    aria-modal="true"
                    className={className}
                    data-test={dataTest || 'modal-wrapper'}
                    ref={contentRef}
                    role="dialog"
                >
                    <Content data-test="modal-content">
                        <Title
                            id="modal-title"
                            ref={titleRef}
                            data-test="modal-title"
                            tabIndex={-1}
                        >
                            {title}
                        </Title>
                        {children}
                        <BaseButtonGroup
                            isCompact={isCompact}
                            primaryButtonProps={primaryButtonProps}
                            secondaryButtonProps={secondaryButtonProps}
                        />
                    </Content>
                    {onClose && (
                        <CloseWrapper
                            aria-label="Close"
                            data-test="modal-close-icon"
                            onClick={onClose}
                            onKeyDown={handleKeyDownCloseIcon}
                            ref={closeRef}
                            role="button"
                            tabIndex={0}
                        >
                            <Close />
                        </CloseWrapper>
                    )}
                </Wrapper>
            </Overlay>
            {useScrollLock && <ScrollLock />}
        </>
    );

    return createPortal(child, domNode);
}

/**
 * ButtonGroup
 * Component that renders 0, 1 or 2 buttons depending on the props.
 * @param {Modal.Button} primaryButtonProps - Props for the primary button
 * @param {Modal.Button} secondaryButtonProps - Props for the secondary button
 * @returns {JSX.Element} ButtonGroup
 */
function BaseButtonGroup({
    isCompact,
    primaryButtonProps,
    secondaryButtonProps,
}) {
    if (typeof primaryButtonProps !== 'object') {
        return null;
    }

    const getPrimaryButton = className => (
        <StyledButton
            className={className}
            $isCompact={isCompact}
            id="modal-primary-button"
            data-test="modal-primary-button"
            onClick={primaryButtonProps.onClick}
            href={primaryButtonProps.href}
            beforeClick={primaryButtonProps.beforeClick}
            disabled={primaryButtonProps.disabled}
            variation={VARIATION.PRIMARY}
        >
            {primaryButtonProps.children}
        </StyledButton>
    );

    const secondaryButton =
        typeof secondaryButtonProps !== 'object' ? null : (
            <StyledButton
                $isCompact={isCompact}
                id="modal-secondary-button"
                data-test="modal-secondary-button"
                onClick={secondaryButtonProps.onClick}
                href={secondaryButtonProps.href}
                beforeClick={secondaryButtonProps.beforeClick}
                disabled={secondaryButtonProps.disabled}
                variation={VARIATION.SECONDARY}
            >
                {secondaryButtonProps.children}
            </StyledButton>
        );

    return isCompact ? (
        <ButtonContainer $isCompact>
            {getPrimaryButton()}
            {secondaryButton}
        </ButtonContainer>
    ) : (
        <ButtonContainer>
            {getPrimaryButton('mobileOnly')}
            {secondaryButton}
            {getPrimaryButton('hideMobile')}
        </ButtonContainer>
    );
}

const Wrapper = styled.div`
    ${breakpoint('xsmall', { down: true })} {
        position: fixed;
        bottom: 0;
        left: 0;
        max-height: 100%;
        width: 100%;

        ${p =>
            !p.$isCompact &&
            css`
                top: 0;
                height: 100%;
            `}
    }

    position: relative;
    color: ${blue100};
    background: ${white};

    ${p =>
        p.$isCompact
            ? css`
                  width: 480px;
                  max-width: 480px;
              `
            : css`
                  width: 560px;
                  max-width: 560px;
              `}
`;

const Content = styled.div`
    ${breakpoint('xsmall', { down: true })} {
        max-height: 100vh;
        padding: 32px 24px 24px 24px;
    }

    box-sizing: border-box;
    padding: 32px;
    max-height: 90vh;
    overflow: auto;
`;

const CloseWrapper = styled.div`
    position: absolute;
    top: 12px;
    right: 12px;
    font-size: 14px;
    cursor: pointer;
    padding: 4px 4px 2px;
    z-index: 9;
`;

const Title = styled.h1`
    ${headingMedium};

    margin-bottom: 24px;

    ${breakpoint.medium} {
        ${headingLarge};
    }
`;

const ButtonContainer = styled.div`
    margin-top: 24px;

    ${breakpoint.small} {
        margin-top: 32px;
    }

    .mobileOnly {
        ${breakpoint.small} {
            display: none;
        }
    }

    .hideMobile {
        ${breakpoint('xsmall', { down: true })} {
            display: none;
        }
    }

    ${p =>
        !p.$isCompact &&
        css`
            ${breakpoint.small} {
                display: flex;
                justify-content: flex-end;
                flex-direction: row;
            }
        `}
`;

const StyledButton = styled(Button)`
    height: 46px;
    ${labelMedium};

    ${breakpoint('xsmall', { down: true })} {
        width: 100%;
        :not(:first-child) {
            margin-top: 12px;
        }
    }

    ${breakpoint.small} {
        ${p =>
            p.$isCompact
                ? css`
                      width: 100%;
                      :not(:first-child) {
                          margin-top: 12px;
                      }
                  `
                : css`
                      :not(:first-child) {
                          margin-left: 12px;
                      }
                  `}
    }
`;

export default withDomNode(InternalModal);
